import { Helmet } from "react-helmet-async";
import { HOTEL } from "@/data/hotel";

const SITE = "https://azulohotels.com";

const defaultDescription =
  "Hotel Azulo Inn — new-age boutique luxury in the heart of Delhi. Part of Marwon Group, operating boutique hotels across India since 1980.";

const DEFAULT_KEYWORDS = [
  "boutique hotels India",
  "luxury boutique hotel Delhi",
  "hotels in Safdarjung Enclave",
  "hotels near Bhikaji Cama Place",
  "Azulo Hotels",
  "Hotel Azulo Inn",
  "boutique hotel New Delhi",
  "best hotels in South Delhi",
].join(", ");

const buildTitle = (rawTitle, title) =>
  rawTitle || (title ? `${title} · ${HOTEL.brand}` : `${HOTEL.name} — Boutique Hotels in Delhi`);

const toAbsoluteImage = (image) => {
  const raw = image || HOTEL.heroImage || HOTEL.logo;
  return raw.startsWith("http") ? raw : `${SITE}${raw}`;
};

export const Seo = ({
  title,
  rawTitle,
  description = defaultDescription,
  path = "",
  image,
  imageAlt,
  type = "website",
  schema,
  keywords = DEFAULT_KEYWORDS,
  noindex = false,
}) => {
  const fullTitle = buildTitle(rawTitle, title);
  const url = `${SITE}${path}`;
  const ogImage = toAbsoluteImage(image);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:site_name" content={HOTEL.brand} />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      <meta name="geo.region" content="IN-DL" />
      <meta name="geo.placename" content="New Delhi" />

      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
};

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "HotelChain",
  name: "Marwon Group",
  alternateName: "Azulo Hotels",
  url: SITE,
  logo: HOTEL.logo,
  foundingDate: "1980",
  description:
    "Marwon Group operates new-age boutique luxury hotels across India. Founded 1980.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "B1/16, Safdarjung Enclave",
    addressLocality: "New Delhi",
    postalCode: "110029",
    addressCountry: "IN",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: HOTEL.phone,
      contactType: "Reservations",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
  ],
  sameAs: [],
};

export const hotelSchema = {
  "@context": "https://schema.org",
  "@type": "Hotel",
  name: HOTEL.name,
  description: defaultDescription,
  url: SITE,
  image: [HOTEL.heroImage],
  telephone: HOTEL.phone,
  email: HOTEL.email,
  priceRange: "₹₹",
  starRating: { "@type": "Rating", ratingValue: "4" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "B1/16, Safdarjung Enclave",
    addressLocality: "New Delhi",
    postalCode: "110029",
    addressCountry: "IN",
  },
  amenityFeature: [
    "Free Wi-Fi",
    "Air conditioning",
    "In-room dining",
    "Restaurant",
    "Garden lawn",
    "24/7 front desk",
    "Parking",
  ].map((n) => ({ "@type": "LocationFeatureSpecification", name: n })),
  numberOfRooms: HOTEL.rooms_count,
  parentOrganization: {
    "@type": "HotelChain",
    name: "Marwon Group",
  },
};

export default Seo;
